import { useState } from 'react';
import { Flame, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { warmImageCache } from '@/lib/api';

/**
 * One-shot admin control that rebuilds the on-disk WebP cache for every
 * product image at all the widths the frontend requests. Idempotent — the
 * backend skips paths whose cache file already exists, so re-clicks are cheap.
 */
export default function WarmImageCacheButton({ variant = 'ghost' }) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    const toastId = toast.loading('Warming image cache — this may take up to a minute…');
    try {
      const res = await warmImageCache();
      const warm = res?.cache_warmup || {};
      const files = res?.files_collection || {};
      const generated = warm.generated ?? 0;
      const skipped = warm.skipped ?? 0;
      const inserted = files.inserted ?? 0;
      toast.success(
        `Cache warmed · ${generated} generated · ${skipped} already cached · ${inserted} files indexed`,
        { id: toastId },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to warm cache';
      toast.error(String(detail), { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  const base =
    'inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
  const style =
    variant === 'primary'
      ? 'bg-[var(--mw-primary)] hover:bg-[var(--mw-primary)]/90 text-white'
      : 'border border-white/15 hover:border-white text-white/80 hover:text-white';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="admin-warm-image-cache"
      title="Rebuild the WebP thumbnail cache for every product image"
      className={`${base} ${style}`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Flame size={14} />}
      {busy ? 'Warming…' : 'Warm Cache'}
    </button>
  );
}
